/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IAny, ICoreContext, IGeneric, msdyn365Commerce } from '@msdyn365-commerce/core';
import { Cart } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { readAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import Cookies from 'js-cookie';
// @ts-ignore
import { claimAnonCart, claimCart, getCartState, ICartState } from '@msdyn365-commerce/global-state';

import resumeCartAsync, { ResumeCartInput } from '../actions/resume-cart.action';
import {
    clearCartData,
    getAuthToken,
    getCartData,
    getCustomerId,
    getGlobantSuiteId,
    getSelectedStoreId,
    removeNewCartPosData,
    setCartData
} from './cookieutils';
import { heldOrderHeaderOf } from './orderchanges';

const CART_COOKIE_NAME = '_msdyn365___cart_';
const RESUME_CART_ID_KEY = 'resumeCartId';

/** Set only while an already placed order is open for modification. hs-cart reads it at checkout. */
export const getResumeCartId = (): string | null => {
    if (!msdyn365Commerce.isBrowser) {
        return null;
    }

    try {
        return localStorage.getItem(RESUME_CART_ID_KEY);
    } catch (error) {
        console.log('LTS Info', 'resumeorderformodify: could not read the handshake', { error });
        return null;
    }
};

export const clearResumeCartId = (): void => {
    if (!msdyn365Commerce.isBrowser) {
        return;
    }

    try {
        localStorage.removeItem(RESUME_CART_ID_KEY);
    } catch (error) {
        console.log('LTS Info', 'resumeorderformodify: could not clear the handshake', { error });
    }

    clearCartData();
};

/** The suite and customer an open order is recorded against, so it is never picked up by another. */
export const handshakeOwner = (): { SuiteId?: string; CustomerId?: string } => ({
    SuiteId: getGlobantSuiteId() || undefined,
    CustomerId: getCustomerId() || undefined
});

/** Whether a handshake was recorded for another suite or another customer than the ones signed in now. */
const isHandshakeForSomeoneElse = (handshake: { SuiteId?: string; CustomerId?: string } | undefined): boolean => {
    const current = handshakeOwner();

    return (
        (!!handshake?.SuiteId && handshake.SuiteId !== current.SuiteId) ||
        (!!handshake?.CustomerId && handshake.CustomerId !== current.CustomerId)
    );
};

/**
 * Whether an order is already open for modification against this channel. A handshake left behind by another
 * suite, another customer or another channel is dropped here rather than reported as open, because nothing
 * else clears it.
 */
export const hasOrderOpenForModify = (channelId: string): boolean => {
    const resumeCartId = getResumeCartId();
    if (!resumeCartId) {
        return false;
    }

    const handshake = getCartData();
    const handshakeChannelId = handshake?.ChannelId;
    if (handshakeChannelId && String(handshakeChannelId) === channelId && !isHandshakeForSomeoneElse(handshake)) {
        return true;
    }

    console.log('LTS Info', 'resumeorderformodify: dropping a handshake that belongs elsewhere', {
        resumeCartId,
        handshakeChannelId,
        channelId,
        handshakeSuiteId: handshake?.SuiteId,
        suiteId: handshakeOwner().SuiteId
    });
    clearResumeCartId();

    return false;
};

/**
 * Whether the handshake describes the cart being acted on. A handshake left behind by an order the till has
 * finished with is dropped here rather than reported as open, because nothing else clears it and the cart it
 * names is not the one in hand.
 */
export const hasOrderOpenForCart = (ecomCartId: string): boolean => {
    const resumeCartId = getResumeCartId();
    if (!resumeCartId) {
        return false;
    }

    const handshakeCartId = getCartData()?.CartId;
    if (!handshakeCartId || String(handshakeCartId) === ecomCartId) {
        return true;
    }

    console.log('LTS Info', 'resumeorderformodify: dropping a handshake that describes a different order', {
        resumeCartId,
        handshakeCartId,
        ecomCartId
    });
    clearResumeCartId();

    return false;
};

/**
 * The name the store holds the order open for modification under, as the handshake recorded it. Undefined
 * when the handshake carries no such name or describes a different order, which is the case for every order
 * placed while the store still composed the name out of this channel's cart id.
 */
export const getOpenOrderStoreCartId = (ecomCartId: string): string | undefined => {
    const handshake = getCartData();

    if (handshake?.StoreCartId && handshake.CartId === ecomCartId) {
        return String(handshake.StoreCartId);
    }

    if (handshake?.CartId && handshake.CartId !== ecomCartId) {
        console.log('LTS Info', 'resumeorderformodify: the handshake describes a different order than the one being checked out', {
            ecomCartId,
            handshakeCartId: handshake.CartId
        });
    }

    return undefined;
};

/**
 * getCartState() is instance-cached per channel/catalog, not per cart id (see storeordersync.ts's
 * readCartBack), and none of its public methods can point an already-created instance at a different cart:
 * refreshCart() always refetches by the id it already has, and updateCart() hard-codes that same id onto
 * whatever is sent. Claiming a resumed cart changes which cart the browser is on, so the already-live
 * singleton is told about it directly here, writing into the same protected, observable field its own
 * refreshCart() writes into internally. This reaches past what the SDK exposes publicly because it exposes
 * no other way to do this; a future @msdyn365-commerce/global-state upgrade could change that field's shape.
 */
const applyResumedCartToSingleton = (cartState: ICartState, resumedCart: Cart): void => {
    ((cartState as unknown) as { _cart: Cart })._cart = resumedCart;
};

const claimResumedCart = async (cartId: string, context: ICoreContext<IGeneric<IAny>>): Promise<void> => {
    const actionContext = context.actionContext;
    const isAuthenticated = context.request.user.isAuthenticated;
    const cartState = await getCartState(actionContext);

    if (isAuthenticated) {
        const claimedCart = await claimCart(cartId, actionContext);
        if (claimedCart?.Id) {
            applyResumedCartToSingleton(cartState, claimedCart);
        }
        return;
    }

    Cookies.set(CART_COOKIE_NAME, encodeURIComponent(`p:${cartId}`), { path: '/' });

    const cartCookie = Cookies.get(CART_COOKIE_NAME) || '';
    const cartCookieParts = cartCookie.split(':');

    const readCart = await readAsync({ callerContext: actionContext }, cartId);
    if (readCart && readCart.Id) {
        context.request.cookies.setCartCookie(readCart, isAuthenticated);
        await claimAnonCart(cartCookieParts, readCart, actionContext);
        applyResumedCartToSingleton(cartState, readCart);
    }
};

const recordHandshake = (cartId: string, storeCartId: string | undefined, resumedCart: Cart): void => {
    const cartLines = resumedCart.CartLines?.map(line => ({
        ItemId: line.ItemId,
        ProductId: line.ProductId,
        LineId: line.LineId,
        Quantity: line.Quantity,
        UnitOfMeasureSymbol: line.UnitOfMeasureSymbol,
        Comments: line.Comment
    }));

    try {
        setCartData({
            CartId: cartId,
            StoreCartId: storeCartId,
            ChannelId: getSelectedStoreId(),
            ...handshakeOwner(),
            cartLines,
            heldOrderHeader: heldOrderHeaderOf(resumedCart)
        });
        localStorage.setItem(RESUME_CART_ID_KEY, cartId);
    } catch (error) {
        console.log('LTS Info', 'resumeorderformodify: could not record the handshake', { cartId, error });
    }
};

/** Records the store's name for a pulled order that could not be opened for changes, so it can be opened later. */
export const recordPulledOrder = (cartId: string, storeCartId: string): void => {
    const handshake = getCartData();
    const kept = handshake?.CartId === cartId ? handshake : {};

    setCartData({ ...kept, CartId: cartId, StoreCartId: storeCartId, ChannelId: getSelectedStoreId() });
};

/** The quantity the store holds for this line, as the handshake recorded it when the order was opened. */
export const getSentQuantityForLine = (lineId: string | undefined): number | undefined => {
    if (!lineId) {
        return undefined;
    }

    const recorded = getCartData()?.cartLines;
    if (!Array.isArray(recorded)) {
        return undefined;
    }

    const sent = recorded.find((line: { LineId?: string }) => line.LineId === lineId);

    return typeof sent?.Quantity === 'number' ? sent.Quantity : undefined;
};

/**
 * Takes a cart back out of the suspended state, without recording that an order is open for
 * modification. Reading the store's copy of an order parks the cart suspended as its last act, so a read
 * that ends without the order being opened leaves a cart the store refuses every write to. This is the
 * call that makes it writable again.
 */
export const makeCartWritable = async (cartId: string, context: ICoreContext<IGeneric<IAny>>): Promise<boolean> => {
    if (!cartId || !msdyn365Commerce.isBrowser) {
        return false;
    }

    try {
        const resumedCart = await resumeCartAsync(
            new ResumeCartInput(cartId, getAuthToken(), getCustomerId() || ''),
            context.actionContext
        );

        return !!resumedCart;
    } catch (error) {
        console.log('LTS Info', 'resumeorderformodify: the cart was left suspended after reading the store', {
            cartId,
            error
        });

        return false;
    }
};

/**
 * Takes a placed order out of the suspended state, makes it the browser's active cart and records the
 * baseline the cart page diffs its line changes against. The handshake is recorded before the cart is
 * claimed: the order is already unsuspended by then, so a later failure must not leave the next checkout
 * thinking it has a fresh cart to place. Resolves undefined when the order could not be opened.
 *
 * The name the store holds the order under is recorded alongside it, because the store mints that name
 * itself and this channel cannot arrive at it from the id its own copy carries.
 */
export const resumeOrderForModify = async (
    cartId: string,
    storeCartId: string | undefined,
    context: ICoreContext<IGeneric<IAny>>
): Promise<Cart | undefined> => {
    if (!cartId || !msdyn365Commerce.isBrowser) {
        return undefined;
    }

    try {
        const resumedCart = await resumeCartAsync(
            new ResumeCartInput(cartId, getAuthToken(), getCustomerId() || ''),
            context.actionContext
        );

        if (!resumedCart) {
            console.log('LTS Info', 'resumeorderformodify: LAC_ITSResumeEcomCart returned no cart', { cartId });
            return undefined;
        }

        recordHandshake(cartId, storeCartId, resumedCart);
        await claimResumedCart(cartId, context);

        return resumedCart;
    } catch (error) {
        console.log('LTS Info', 'resumeorderformodify: failed to open the order for modification', { cartId, error });
        return undefined;
    } finally {
        removeNewCartPosData();
    }
};
